import clsx from 'clsx';
import { type ReactElement } from 'react';

import { DatePicker, Input, Radio, Select, Textarea } from '~components/generic/elements';
import { type ApiMediaUploadSettings, type FieldSettings, type FieldValue } from '~modules/jsm-contact/shared/types';
import { IValueOptionsShape } from '~services/entity-definitions/interface';
import { CheckboxGroup } from './CheckboxGroup';
import { UploadGroup } from './UploadGroup';

type FormFieldProps = {
  fieldSettings: FieldSettings;
  fieldValue: FieldValue | FieldValue[];
  error?: string | null;
  onValueChanged: (key: string, value: FieldValue | FieldValue[]) => void;
  onError: () => void;
  isSubmitting: boolean;
  dataKey: string;
  apiMediaUploadSettings?: ApiMediaUploadSettings;
};

export const createFormField = ({
  fieldSettings,
  fieldValue,
  onValueChanged,
  error,
  onError,
  isSubmitting,
  dataKey,
  apiMediaUploadSettings,
}: FormFieldProps): ReactElement | null => {
  switch (fieldSettings.type) {
    case 'text':
    case 'email': {
      return (
        <Input
          name={fieldSettings.name}
          type={fieldSettings.type}
          value={
            fieldValue !== undefined
              ? String(fieldValue as FieldValue)
              : fieldSettings.defaultValue !== undefined
                ? String(fieldSettings.defaultValue)
                : undefined
          }
          // defaultValue={fieldSettings.defaultValue !== undefined ? String(fieldSettings.defaultValue) : undefined} <- not needed, use 'value'
          placeholder={fieldSettings.description}
          required={fieldSettings.required}
          onChange={(value: string) => onValueChanged(fieldSettings.name, value as FieldValue)}
          disabled={isSubmitting}
          className={clsx('u-block', 'u-full-width', error && 'error')}
        />
      );
    }

    case 'textarea': {
      return (
        <Textarea
          name={fieldSettings.name}
          value={
            fieldValue !== undefined
              ? String(fieldValue as FieldValue)
              : fieldSettings.defaultValue !== undefined
                ? String(fieldSettings.defaultValue)
                : undefined
          }
          // defaultValue={fieldSettings.defaultValue !== undefined ? String(fieldSettings.defaultValue) : undefined} <- not needed, use 'value'
          placeholder={fieldSettings.description}
          required={fieldSettings.required}
          // autosize
          // resizable
          onChange={(value: string) => onValueChanged(fieldSettings.name, value)}
          disabled={isSubmitting}
          className={clsx('form-control', 'w-full', error && 'error')}
        />
      );
    }

    case 'select': {
      const items = getSelectItems(fieldSettings);

      return (
        <Select
          name={fieldSettings.name}
          value={fieldValue !== undefined ? String(fieldValue as FieldValue) : undefined}
          defaultValue={fieldSettings.defaultValue !== undefined ? String(fieldSettings.defaultValue) : undefined}
          onChange={(selectedValue: string | number | null) => onValueChanged(fieldSettings.name, selectedValue)}
          disabled={isSubmitting}
          required={fieldSettings.required}
          options={items}
          className={clsx('u-block', 'u-full-width', error && 'error')}
        />
      );
    }

    case 'checkbox-group': {
      return (
        <CheckboxGroup
          fieldValue={fieldValue}
          fieldSettings={fieldSettings}
          onValueChanged={onValueChanged}
          isSubmitting={isSubmitting}
          error={error}
        />
      );
    }

    case 'radio': {
      const items = getRadioItems(fieldSettings);

      return (
        <Radio
          name={fieldSettings.name}
          value={
            fieldValue !== undefined
              ? String(fieldValue as FieldValue)
              : fieldSettings.defaultValue !== undefined
                ? String(fieldSettings.defaultValue)
                : ''
          }
          // defaultValue={fieldSettings.defaultValue !== undefined ? String(fieldSettings.defaultValue) : ''} <- not needed, use 'value'
          onChange={(selectedValue: string) => onValueChanged(fieldSettings.name, selectedValue)}
          disabled={isSubmitting}
          required={fieldSettings.required}
          values={items}
        />
      );
    }

    case 'file': {
      return (
        <UploadGroup
          fieldSettings={fieldSettings}
          fieldValue={fieldValue as FieldValue}
          onValueChanged={onValueChanged}
          isSubmitting={isSubmitting}
          onError={onError}
          dataKey={dataKey}
          apiMediaUploadSettings={apiMediaUploadSettings}
        />
      );
    }

    case 'date': {
      return (
        <DatePicker
          name={fieldSettings.name}
          defaultValue={fieldSettings.defaultValue !== undefined ? String(fieldSettings.defaultValue) : ''}
          placeHolder={fieldSettings.description ? fieldSettings.description : 'Select a date'}
          dateFormat="yyyy-MM-dd"
          minDate={null}
          maxDate={null}
          onChange={(value: string) => onValueChanged(fieldSettings.name, value)}
          disabled={isSubmitting}
          required={fieldSettings.required}
          className={clsx('u-block', 'u-full-width', error && 'error')}
          // isClearable
          // shouldCloseOnSelect
          // variant="single"
        />
      );
    }

    default: {
      console.error('createFormField: Unsupported element type:', fieldSettings.type);
      return null;
    }
  }
};

const getSelectItems = (fieldSettings: FieldSettings): IValueOptionsShape => {
  if (fieldSettings.options) {
    const items = fieldSettings.options.map(option => {
      const label = option.label !== undefined ? String(option.label) : '';

      return {
        label,
        value: String(option.value),
      };
    });

    return fieldSettings.defaultValue === undefined
      ? [
          { label: fieldSettings.description ? fieldSettings.description : 'Please select', value: '' as FieldValue },
        ].concat(items)
      : items;
  }
  return [];
};

const getRadioItems = (fieldSettings: FieldSettings) => {
  if (fieldSettings.options) {
    return fieldSettings.options.map(option => {
      const label = option.label !== undefined ? String(option.label) : '';

      return {
        label,
        value: String(option.value),
      };
    });
  }
  return [];
};
